import { format, useCurrenciesContext } from '@talos/kyoko';
import type { BigSource } from 'big.js';
import type React from 'react';
import { memo } from 'react';

export type InlineFormattedNumberProps = {
  number?: BigSource;
  increment?: string;
  currency?: string | null;
  truncate?: boolean;
  round?: boolean;
  pretty?: boolean;
  showCurrency?: boolean;
  showCurrencySymbol?: boolean;
};

const currencySymbolMap = {
  USD: '$',
  PHP: '₱',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  IDR: 'Rp',
  MYR: 'RM',
  SGD: 'S$',
  THB: '฿',
  VND: '₫',
  INR: '₹',
  KRW: '₩',
  HKD: 'HK$',
  MXN: 'MX$',
  NZD: 'NZ$',
  CHF: 'CHF',
  CNY: '¥',
  RUB: '₽',
  ZAR: 'R',
};

const currencyToSymbol = (currency: string) => {
  return currencySymbolMap[currency as keyof typeof currencySymbolMap] ?? currency;
};

export const InlineFormattedNumber: React.FC<InlineFormattedNumberProps> = memo(props => {
  const {
    number,
    increment: _increment,
    currency,
    truncate = true,
    pretty = true,
    round = false,
    showCurrency = true,
    showCurrencySymbol = false,
    ...rest
  } = props;
  const { currenciesBySymbol } = useCurrenciesContext();

  const currencyInfo = currenciesBySymbol.get(currency ?? '');
  const increment = _increment ?? currencyInfo?.DefaultIncrement;

  if (number == null) {
    return null;
  }

  const numberString = format(number.toString(), {
    spec: increment,
    truncate,
    round,
    pretty,
  });

  return (
    <span className="flex gap-1">
      {currency && showCurrency && <span>{showCurrencySymbol ? currencyToSymbol(currency) : currency}</span>}
      <span {...rest}>{numberString}</span>
    </span>
  );
});
